/**
 * Which learner bubbles get the discreet "no important errors" ✓ (D10, owner
 * 2026-10-06/07: "later, and in a discreet form: no sound, only on sentences
 * of 5+ words, meaning 'no important errors'"). Pure: the whole thread in, the
 * indexes of the bubbles that show it out. Derived on every render, so a
 * correction that lands after the ✓ (the in-reply correction tool, a
 * re-delivered message) takes it back by itself.
 *
 * The rule itself (2–4 below) is {@link earnsCleanTick}, shared by the live
 * thread ({@link selectCleanSentenceTicks}) and the history's session detail
 * ({@link selectTranscriptCleanTicks}, from the verdict persisted per turn).
 *
 * A bubble gets it only when ALL of these hold:
 *
 *   1. It is a learner bubble. In the live thread that includes the history
 *      seeded by "Continuar" or a reload: its bubbles carry the turn's
 *      corrections and its PERSISTED verdict (`historicalTurn`, from the
 *      transcript) — a turn before migration 0393 has none and never shows it.
 *   2. Its corrections verdict is `ok`. `failed` / `skipped` (a superseded
 *      turn, a game turn, A0's objectives-only call, an "Inspirar" turn) and an
 *      absent verdict (still analysing, or a lost `corrections.done`) all mean
 *      "we do not know" — never a clean sentence.
 *   3. It has at least {@link CLEAN_TICK_MIN_WORDS} words as the bubble shows
 *      them: 97% of a B1 learner's 1–2-word sentences have no errors, so a ✓
 *      there means nothing (decision brief, D10).
 *   4. The analysis found no IMPORTANT error: no grammar correction the
 *      `essential` sensitivity keeps — `structural` (GRAMÁTICA) or
 *      `communication-breaking` (ERROR GRAVE), the two top tiers of the
 *      severity model (`errorModel.ts` / `severityStyle.ts`); a correction
 *      without a severity counts as `structural`, as everywhere else — and no
 *      pronunciation correction. `minor` (DETALLE) and `suggestion` (MÁS
 *      NATURAL) notes still allow it. Every correction counts, whatever the
 *      learner's sensitivity level hides.
 *   5. The bubble shows no positive skill chip (`selectThreadSkillChips`): the chip
 *      — a rep, "lo clavaste", a completion — is the stronger signal, and the
 *      two are never stacked. A chip that arrives after the ✓ replaces it.
 *
 * And the surface allows it at all ({@link CleanTickSurface}).
 */
import type { CorrectionLevel, CorrectionsVerdict } from '@orborix/shared-types';
import type { VoiceMessage } from '@orborix/voice-client';
import type { TranscriptTurn } from '../../lib/sessionsApi.js';
import {
  buildErrors,
  buildErrorsFromTranscript,
  filterErrorsByLevel,
  type ConvError,
} from './errorModel.js';

/** Shortest learner sentence (in shown words) that can earn the ✓. */
export const CLEAN_TICK_MIN_WORDS = 5;

/**
 * The sensitivity level whose errors are "important": an error this level
 * still shows withholds the ✓ (structural and communication-breaking grammar,
 * every pronunciation correction).
 */
export const IMPORTANT_ERRORS_LEVEL: CorrectionLevel = 'essential';

/** Where the thread is shown: the ✓ exists only in plain live conversation. */
export interface CleanTickSurface {
  /**
   * The learner's corrections view is on (`settings.correction`). Off, the
   * thread shows no evaluation at all — and a ✓ on some sentences would point
   * at the errors of the others.
   */
  correctionsOn: boolean;
  /**
   * A game is running (`isGameSeed`): its correction chips are withheld so as
   * not to give the answer away (plan 42 §9.1), and so is the ✓. Game turns
   * already carry a `skipped` verdict; this also covers a turn the server did
   * not tag.
   */
  inGame: boolean;
  /**
   * An A0 guided chat (`a0Mission`): corrections are off there by design, so
   * nothing vouches for a sentence. Its turns carry `skipped` verdicts anyway.
   */
  a0Mission: boolean;
}

/**
 * Words in a sentence as the bubble shows it: whitespace-separated tokens that
 * carry at least one letter or digit (a dash or an ellipsis is not a word; a
 * contraction or a number is one).
 */
export function countShownWords(text: string): number {
  return text.split(/\s+/u).filter((token) => /[\p{L}\p{N}]/u.test(token)).length;
}

/**
 * The ✓ rule on one learner sentence (rules 2–4), wherever it comes from:
 * `verdict` is the turn's corrections verdict status (`null` / `undefined` =
 * unknown), `errors` EVERY correction of the turn — unfiltered by the
 * learner's sensitivity level.
 */
export function earnsCleanTick(input: {
  text: string;
  verdict: CorrectionsVerdict['status'] | null | undefined;
  errors: ReadonlyArray<ConvError>;
}): boolean {
  if (input.verdict !== 'ok') return false;
  if (countShownWords(input.text) < CLEAN_TICK_MIN_WORDS) return false;
  return filterErrorsByLevel(input.errors, IMPORTANT_ERRORS_LEVEL).length === 0;
}

/**
 * Whether ONE bubble of the thread earns the ✓ by its own content (rules 1–4):
 * a live bubble by its live verdict, a historical one by the verdict persisted
 * for its turn (none = unknown).
 */
export function isCleanSentence(m: VoiceMessage): boolean {
  if (m.who !== 'user') return false;
  return earnsCleanTick({
    text: m.text,
    verdict: m.historical
      ? (m.historicalTurn?.correctionsVerdict ?? null)
      : m.correctionsVerdict?.status,
    errors: buildErrors(m),
  });
}

/**
 * Index (in `messages`) of every bubble that shows the ✓. `skillChips` is the
 * thread's chip map (`selectThreadSkillChips`, live and historical): a bubble
 * with a chip never gets it.
 */
export function selectCleanSentenceTicks(
  messages: ReadonlyArray<VoiceMessage>,
  skillChips: ReadonlyMap<number, unknown>,
  surface: CleanTickSurface,
): ReadonlySet<number> {
  const out = new Set<number>();
  if (!surface.correctionsOn || surface.inGame || surface.a0Mission) return out;
  messages.forEach((m, i) => {
    if (!skillChips.has(i) && isCleanSentence(m)) out.add(i);
  });
  return out;
}

/**
 * Turn index of every past turn that shows the ✓ in the history's session
 * detail: the same rule as live ({@link earnsCleanTick}) on the verdict the
 * transcript carries per turn — a turn before migration 0393 has none and
 * never shows it; game, A0 and "Inspirar" turns are `skipped`. `skillChips` is
 * the transcript's chip map (`selectTranscriptSkillChips`): the chip wins.
 *
 * No surface switch: the history always shows the turn's corrections (only
 * filtered by sensitivity), so the ✓ never stands alone among hidden errors.
 */
export function selectTranscriptCleanTicks(
  turns: ReadonlyArray<TranscriptTurn>,
  skillChips: ReadonlyMap<number, unknown>,
): ReadonlySet<number> {
  const out = new Set<number>();
  for (const t of turns) {
    if (!t.userText || skillChips.has(t.turnIndex)) continue;
    const clean = earnsCleanTick({
      text: t.userText,
      verdict: t.correctionsVerdict,
      errors: buildErrorsFromTranscript(t.corrections, t.turnIndex),
    });
    if (clean) out.add(t.turnIndex);
  }
  return out;
}
